import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Card, Muted } from "@/components/ui";
import { isNegative, money, shortDate } from "@/lib/format";
import { type SavedCalculation, deleteSaved, listSaved } from "@/lib/saved";
import { KEYS } from "@/lib/storage";
import { useCalculator } from "@/state/calculator";

export default function SavedPage() {
  const navigate = useNavigate();
  const { load } = useCalculator();
  const [items, setItems] = useState<SavedCalculation[]>(listSaved);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const refresh = useCallback(() => setItems(listSaved()), []);

  // Keep in step with other tabs of NetKeep open in this browser.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEYS.saved || e.key == null) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  const open = (item: SavedCalculation) => {
    load(item.values, item.profileId);
    void navigate("/");
  };

  const remove = (id: string) => {
    try {
      deleteSaved(id);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setPendingDelete(null);
    refresh();
  };

  return (
    <>
      <h1 className="page-title">Saved calculations</h1>
      <Muted>Stored only in this browser. Open one to load it into the calculator with today's fee data.</Muted>
      {error ? <p className="text-error">{error}</p> : null}
      {items.length === 0 ? (
        <Card>
          <Muted>Nothing saved yet. Use “Save calculation” on the Calculator tab.</Muted>
        </Card>
      ) : null}
      {items.map((item) => (
        <Card key={item.id}>
          <button
            type="button"
            className="saved-open"
            onClick={() => open(item)}
            aria-label={`${item.title}, profit ${money(item.netProfit, item.currency)}. Load into the calculator.`}
          >
            <span className="grow">
              <span className="item__title block">
                {item.title}
              </span>
              <span className="muted">
                {item.profileName} · {shortDate(item.createdAt)}
              </span>
            </span>
            <span className="stack-xs align-end">
              <span className={`item__profit ${isNegative(item.netProfit) ? "is-negative" : "is-profit"}`}>
                {money(item.netProfit, item.currency)}
              </span>
              <span className="muted">profit</span>
            </span>
          </button>
          {pendingDelete === item.id ? (
            <div className="row">
              <Button title="Delete" variant="danger" onClick={() => remove(item.id)} flex />
              <Button title="Keep" variant="secondary" onClick={() => setPendingDelete(null)} flex />
            </div>
          ) : (
            <Button title="Delete…" variant="ghost" onClick={() => setPendingDelete(item.id)} />
          )}
        </Card>
      ))}
    </>
  );
}
